import { useCallback, useState } from 'react'
import { isNotDefined, isNotEmpty, type GameGetSuccess, type GameData, type UnsavedGame } from '@terros/common'
import { useGameCache } from '../cache'
import { useAddGame } from '../add'
import { useTranslation } from '../../i18n'
import { basicS3Upload } from '../../basicS3Upload'
import { fetchImageBlob, useClonePrizeImages } from './useClonePrizeImages'
import { useCloneParticipants } from './useCloneParticipants'
import { toClonedGame } from './transform'

type Return = {
  clone: (game: GameGetSuccess, options: CloneOptions) => Promise<GameData | undefined>
  isLoading: boolean
  error: string | undefined
}

type CloneOptions = {
  newStartDate: number
  copyParticipants?: boolean
  /**
   * Called imperatively when the game was created but some follow-up copies failed.
   * Use it to show a toast: the caller usually navigates away (unmounting anything
   * that reads `error`) as soon as `clone` resolves.
   */
  onPartialFailure?: (message: string) => void
}

export function useCloneGame(): Return {
  const [isLoading, setIsLoading] = useState(false)
  const [copyError, setCopyError] = useState<string>()
  const { t } = useTranslation()
  const cache = useGameCache()
  const { add, error: addGameError } = useAddGame()
  const { clone: cloneParticipants } = useCloneParticipants()
  const { clone: clonePrizeImages } = useClonePrizeImages()

  const cloneCover = useCallback(
    async (prevGame: GameData, newGame: GameData) => {
      if (!prevGame.cover?.blurhash || !prevGame.cover.download || !newGame.cover?.upload) return

      const blob = await fetchImageBlob(prevGame.cover.download)
      const file = new File([blob], 'cover.jpg', { type: 'image/jpeg' })
      await basicS3Upload(newGame.cover?.upload, file)
      await new Promise((resolve) => setTimeout(resolve, 3000))
      cache.updateCover(newGame.gameId)
    },
    [cache]
  )

  const clone = useCallback(
    async (
      gameData: GameGetSuccess,
      { newStartDate, copyParticipants, onPartialFailure }: CloneOptions
    ): Promise<GameData | undefined> => {
      const { game } = gameData
      setCopyError(undefined)
      const unsavedGame = toValidClonedGame(game, newStartDate)
      if (isNotDefined(unsavedGame)) {
        setCopyError(t('This game has invalid data and cannot be copied'))
        return undefined
      }
      try {
        setIsLoading(true)
        const newGame = await add(unsavedGame)
        // The game now exists, so always return it (and let the caller navigate to it)
        // even if some of the follow-up copies fail.
        const results = await Promise.allSettled([
          cloneCover(game, newGame),
          clonePrizeImages(game, newGame),
          copyParticipants ? cloneParticipants(newGame, gameData) : Promise.resolve(),
        ])
        const failed = results.flatMap((result, index) =>
          result.status === 'rejected' ? [[t('cover image'), t('prize images'), t('participants')][index]] : []
        )
        if (isNotEmpty(failed)) {
          const message = t('Game copied, but some items failed to copy: {{items}}', { items: failed.join(', ') })
          // Prefer the imperative callback; only fall back to `error` state when none is given,
          // so callers that also toast `error` don't show it twice.
          if (onPartialFailure) onPartialFailure(message)
          else setCopyError(message)
        }
        return newGame
      } catch {
        return undefined
      } finally {
        setIsLoading(false)
      }
    },
    [add, cloneCover, cloneParticipants, clonePrizeImages, t]
  )
  return { clone, isLoading, error: copyError || addGameError }
}

const toValidClonedGame = (game: GameData, newStartDate: number): UnsavedGame | undefined => {
  try {
    return toClonedGame(game, newStartDate)
  } catch {
    return undefined
  }
}
